import { useRef, useState } from "react";
import api, { useList, fmtRM, errMsg, apiFileUrl } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Download,
  Trash2, ShieldCheck, ShieldAlert, ChevronDown, ChevronRight,
} from "lucide-react";
import { MigrationReviewPanel } from "@/components/import/MigrationReviewPanel";

const FILE_LABELS = {
  courses_fees_master: "Course Catalogue",
  courses_active: "Active Courses",
  teachers: "Teachers",
  studios: "Studios",
  students_master: "Students Master",
  school_system_export: "Yamaha System Export",
  teacher_weekly_schedule: "Teacher Timetable",
  studio_occupancy: "Studio Bookings",
  payments_ledger: "Payments Ledger",
  unpaid_list: "Unpaid List",
};

const StatusPill = ({ status }) => {
  const map = {
    valid: "bg-emerald-100 text-emerald-800",
    warning: "bg-amber-100 text-amber-800",
    error: "bg-red-100 text-red-800",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}>{status}</span>;
};

function Section({ title, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border bg-card mb-4" data-testid={testId}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
        data-testid={`${testId}-toggle`}>
        <span className="font-semibold">{title}</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

const KV = ({ label, value, accent }) => (
  <div className="rounded-md border bg-background p-3">
    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-lg font-bold font-display ${accent || ""}`}>{value}</p>
  </div>
);

function RowIssuesViewer({ batchId, fileKey }) {
  const [status, setStatus] = useState("error");
  const { data, loading } = useList(
    `/admin/import/batches/${batchId}/rows/${fileKey}?status=${status}&page_size=100`, [status]);
  return (
    <div className="mt-3">
      <div className="flex gap-2 mb-2">
        {["error", "warning", "valid"].map((s) => (
          <Button key={s} size="sm" variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)} data-testid={`rows-filter-${s}`}>{s}</Button>
        ))}
      </div>
      {loading ? <Spinner /> : !data?.rows?.length ? <EmptyState text={`No ${status} rows.`} /> : (
        <div className="max-h-80 overflow-auto rounded border text-xs">
          {data.rows.map((r) => (
            <div key={r.id} className="border-b px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold">Row {r.row_number}</span>
                <StatusPill status={r.validation_status} />
                <span className="text-muted-foreground truncate">
                  {Object.values(r.original_data).filter(Boolean).slice(0, 3).join(" · ")}
                </span>
              </div>
              {r.error_messages.map((m, i) => (
                <p key={i} className="text-red-700 mt-1">✗ {m}</p>
              ))}
              {r.warning_messages.map((m, i) => (
                <p key={i} className="text-amber-700 mt-1">⚠ {m}</p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DataImportPage() {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [batch, setBatch] = useState(null);
  const [openIssues, setOpenIssues] = useState(null);
  const { data: batches, reload } = useList("/admin/import/batches");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/import/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(r.data);
      reload();
      toast.success("ZIP staged & validated — review the preview below. No live data was changed.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadBatch = async (id) => {
    const r = await api.get(`/admin/import/batches/${id}`);
    setBatch(r.data);
  };

  const discard = async (id) => {
    if (!window.confirm("Discard this staged batch? Live data is not affected.")) return;
    try {
      await api.delete(`/admin/import/batches/${id}`);
      if (batch?.id === id) setBatch(null);
      reload();
      toast.success("Batch discarded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const applyImport = async () => {
    if (!window.confirm("FINAL IMPORT: apply this staged batch to the live system?\n\nTeachers, students, parents, courses, studios, schedules, invoices and payments will be created. Existing records are never deleted. You can roll back afterwards.")) return;
    setApplying(true);
    try {
      const r = await api.post(`/admin/import/batches/${batch.id}/apply`);
      toast.success("Final import applied successfully");
      await loadBatch(batch.id);
      reload();
      return r;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApplying(false);
    }
  };

  const rollback = async () => {
    if (!window.confirm("Roll back this import? Every record created by this batch will be removed from the live system. Staged data is kept so you can re-apply.")) return;
    setApplying(true);
    try {
      await api.post(`/admin/import/batches/${batch.id}/rollback`);
      toast.success("Import rolled back — live records removed");
      await loadBatch(batch.id);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApplying(false);
    }
  };

  const rep = batch?.report;
  const safe = rep?.safe_to_import;

  return (
    <div data-testid="data-import-page">
      <PageHeader title="Data Import" description="Upload the school-data CSV ZIP for staged preview, validation, and safe migration. Nothing touches live data until you confirm final import." />

      {/* upload */}
      <div className="rounded-lg border-2 border-dashed bg-card p-8 text-center mb-6">
        <UploadCloud className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground mb-3">Upload a .zip containing the 10 school CSV files (nested ZIPs are handled)</p>
        <input ref={fileRef} type="file" accept=".zip" className="hidden"
          onChange={(e) => upload(e.target.files[0])} data-testid="import-zip-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="import-upload-btn">
          {uploading ? "Staging & validating…" : "Choose ZIP file"}
        </Button>
      </div>

      {/* previous batches */}
      {batches?.length > 0 && (
        <div className="rounded-lg border bg-card p-4 mb-6" data-testid="import-batches-list">
          <p className="text-sm font-semibold mb-2">Staged batches</p>
          {batches.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
              <span className="truncate">{b.source_zip} <span className="text-muted-foreground">· {new Date(b.created_at).toLocaleString()}</span></span>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{b.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => loadBatch(b.id)} data-testid={`batch-view-${b.id}`}>View</Button>
                <Button size="sm" variant="ghost" onClick={() => discard(b.id)} data-testid={`batch-discard-${b.id}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rep && (
        <div data-testid="import-preview-report">
          {/* verdict banner */}
          <div className={`rounded-lg border p-5 mb-6 flex items-start gap-3 ${safe.safe ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}
            data-testid="import-safety-verdict">
            {safe.safe ? <ShieldCheck className="h-6 w-6 text-emerald-700 shrink-0" /> : <ShieldAlert className="h-6 w-6 text-red-700 shrink-0" />}
            <div>
              <p className="font-bold">{safe.verdict}</p>
              {safe.critical_blockers.map((b, i) => (
                <p key={i} className="text-sm mt-1 text-muted-foreground">• {b}</p>
              ))}
              <p className="text-xs mt-2 font-semibold text-muted-foreground">
                {batch.status === "imported"
                  ? `Final import applied ${batch.applied_at ? `on ${new Date(batch.applied_at).toLocaleString()}` : ""} — see the result section below. Rollback available.`
                  : "Preview only — no final import has been applied. Use the Apply Final Import button in section G."}
              </p>
            </div>
          </div>

          <MigrationReviewPanel batch={batch} applying={applying} onApply={applyImport} />

          {/* A. file detection */}
          <Section title="A · File Detection" defaultOpen testId="section-files">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
                  <th className="py-2 pr-3">File</th><th className="pr-3">Rows</th><th className="pr-3">Headers</th>
                  <th className="pr-3">Valid</th><th className="pr-3">Warn</th><th className="pr-3">Error</th>
                  <th className="pr-3">Exports</th><th>Issues</th>
                </tr></thead>
                <tbody>
                  {rep.file_detection.found.map((fd) => {
                    const v = rep.validation_summary.per_file[fd.file_key] || {};
                    return (
                      <tr key={fd.file_key} className="border-b last:border-0" data-testid={`file-row-${fd.file_key}`}>
                        <td className="py-2 pr-3 font-medium whitespace-nowrap">
                          <FileSpreadsheet className="h-4 w-4 inline mr-1 text-muted-foreground" />
                          {FILE_LABELS[fd.file_key] || fd.filename}
                          <span className="block text-xs text-muted-foreground font-mono">{fd.filename}</span>
                        </td>
                        <td className="pr-3">{fd.rows}</td>
                        <td className="pr-3">{fd.headers_match
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          : <XCircle className="h-4 w-4 text-red-600" />}</td>
                        <td className="pr-3 text-emerald-700">{v.valid_rows}</td>
                        <td className="pr-3 text-amber-700">{v.warning_rows}</td>
                        <td className="pr-3 text-red-700 font-semibold">{v.error_rows}</td>
                        <td className="pr-3 whitespace-nowrap">
                          {["raw", "errors", "correction", "template"].map((k) => (
                            <a key={k} className="text-xs underline text-primary mr-2"
                              href={apiFileUrl(`/admin/import/batches/${batch.id}/export/${fd.file_key}?kind=${k}`)}
                              data-testid={`export-${fd.file_key}-${k}`}>
                              <Download className="h-3 w-3 inline" /> {k}
                            </a>
                          ))}
                        </td>
                        <td>
                          {(v.error_rows > 0 || v.warning_rows > 0) && (
                            <Button size="sm" variant="outline"
                              onClick={() => setOpenIssues(openIssues === fd.file_key ? null : fd.file_key)}
                              data-testid={`issues-btn-${fd.file_key}`}>
                              {openIssues === fd.file_key ? "Hide" : "Review"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {openIssues && <RowIssuesViewer batchId={batch.id} fileKey={openIssues} />}
            {rep.file_detection.missing_files.length > 0 && (
              <p className="text-sm text-red-700 mt-2">Missing files: {rep.file_detection.missing_files.join(", ")}</p>
            )}
            {rep.file_detection.extra_non_csv_files.length > 0 && (
              <p className="text-sm text-muted-foreground mt-2">Extra non-CSV files (ignored): {rep.file_detection.extra_non_csv_files.join(", ")}</p>
            )}
          </Section>

          {/* B. data summary */}
          <Section title="B · Data Summary" defaultOpen testId="section-data">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(rep.data_summary).map(([k, v]) => (
                <KV key={k} label={k.replaceAll("_", " ")} value={v} />
              ))}
            </div>
          </Section>

          {/* C. finance summary */}
          <Section title="C · Finance Summary" defaultOpen testId="section-finance">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <KV label="Total billed" value={fmtRM(rep.finance_summary.total_billed)} />
              <KV label="Total paid" value={fmtRM(rep.finance_summary.total_paid)} accent="text-emerald-700" />
              <KV label="Total unpaid" value={fmtRM(rep.finance_summary.total_unpaid)} accent="text-red-700" />
              <KV label="Unpaid list outstanding" value={fmtRM(rep.finance_summary.unpaid_list_outstanding_total)} accent="text-red-700" />
              <KV label="Waived" value={fmtRM(rep.finance_summary.total_waived)} />
              <KV label="Deposit offset" value={fmtRM(rep.finance_summary.total_deposit_offset)} />
              <KV label="Estimated SST billed" value={fmtRM(rep.finance_summary.estimated_sst_billed)} />
              <KV label="SST collected (paid)" value={fmtRM(rep.finance_summary.sst_collected_on_paid)} />
              <KV label="Commission base (music fee collected)" value={fmtRM(rep.finance_summary.commission_base_music_fee_collected)} />
              <KV label="Estimated teacher commission" value={fmtRM(rep.finance_summary.estimated_teacher_commission)} />
            </div>
            <p className="text-xs text-muted-foreground">{rep.finance_summary.commission_rule}</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
                  <th className="py-1 pr-3">Status</th><th className="pr-3">Rows</th><th className="pr-3">Billed</th><th className="pr-3">Paid</th><th>Maps to</th>
                </tr></thead>
                <tbody>
                  {Object.entries(rep.status_mapping_preview).map(([s, m]) => (
                    <tr key={s} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 font-medium">{s}</td>
                      <td className="pr-3">{m.ledger_rows}</td>
                      <td className="pr-3">{fmtRM(rep.finance_summary.by_status[s]?.billed || 0)}</td>
                      <td className="pr-3">{fmtRM(rep.finance_summary.by_status[s]?.paid || 0)}</td>
                      <td className="text-xs text-muted-foreground">{m.maps_to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* D. occupancy */}
          <Section title="D · Studio Occupancy Summary" testId="section-occupancy">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KV label="Busiest studio" value={`Studio ${rep.occupancy_summary.busiest_studio?.studio} · ${rep.occupancy_summary.busiest_studio?.utilization_pct}%`} />
              <KV label="Most underused" value={`Studio ${rep.occupancy_summary.most_underused_studio?.studio} · ${rep.occupancy_summary.most_underused_studio?.utilization_pct}%`} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left uppercase text-muted-foreground border-b">
                  <th className="py-1 pr-2">Studio</th>
                  {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => <th key={d} className="pr-2">{d.slice(0,3)}</th>)}
                  <th>Total/wk</th>
                </tr></thead>
                <tbody>
                  {Object.entries(rep.occupancy_summary.per_studio_day_minutes).map(([s, days]) => (
                    <tr key={s} className="border-b last:border-0">
                      <td className="py-1 pr-2 font-medium">{s}</td>
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => (
                        <td key={d} className="pr-2">{days[d] || "—"}</td>
                      ))}
                      <td className="font-semibold">{rep.occupancy_summary.weekly_minutes_by_studio[s]} min</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rep.occupancy_summary.conflict_blocks.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-semibold text-amber-700"><AlertTriangle className="h-4 w-4 inline mr-1" />Conflict blocks</p>
                {rep.occupancy_summary.conflict_blocks.map((c, i) => <p key={i} className="text-xs text-muted-foreground">• {c}</p>)}
              </div>
            )}
          </Section>

          {/* E. validation totals */}
          <Section title="E · Validation Summary" defaultOpen testId="section-validation">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KV label="Total rows" value={rep.validation_summary.totals.total_rows} />
              <KV label="Valid rows" value={rep.validation_summary.totals.valid_rows} accent="text-emerald-700" />
              <KV label="Warning rows" value={rep.validation_summary.totals.warning_rows} accent="text-amber-700" />
              <KV label="Error rows" value={rep.validation_summary.totals.error_rows} accent="text-red-700" />
              <KV label="Duplicate-flagged" value={rep.validation_summary.totals.duplicate_rows} />
              <KV label="Missing references" value={rep.validation_summary.totals.missing_reference_rows} />
              <KV label="Conflict rows" value={rep.validation_summary.totals.conflict_rows} />
              <KV label="Receipts shared across students" value={rep.validation_summary.shared_receipts_multi_student} />
            </div>
          </Section>

          {/* F. recommended fixes */}
          <Section title="F · Recommended Fixes" defaultOpen testId="section-fixes">
            {rep.recommended_fixes.length === 0 ? <EmptyState text="No fixes needed." /> :
              rep.recommended_fixes.map((fx, i) => (
                <div key={i} className="rounded-md border bg-background p-3 mb-2">
                  <p className="text-sm font-semibold">{fx.issue} <Badge variant="outline">{fx.count}</Badge></p>
                  <p className="text-xs text-muted-foreground mt-1">{fx.fix}</p>
                  {fx.details && (
                    <p className="text-xs font-mono mt-1 text-muted-foreground break-all">
                      {Array.isArray(fx.details)
                        ? fx.details.slice(0, 10).map((d) => typeof d === "string" ? d : JSON.stringify(d)).join(" · ")
                        : Object.entries(fx.details).slice(0, 12).map(([k, v]) => `${k}→${v}`).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
          </Section>

          {/* default charges */}
          <Section title="Default Enrolment Charges (preview)" testId="section-charges">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <KV label="Registration before SST" value={fmtRM(rep.default_charges_preview.registration_fee_before_sst)} />
              <KV label="SST 8%" value={fmtRM(rep.default_charges_preview.registration_fee_sst)} />
              <KV label="Registration after SST" value={fmtRM(rep.default_charges_preview.registration_fee_after_sst)} />
              <KV label="Default admin fee" value={fmtRM(rep.default_charges_preview.default_school_admin_fee)} />
            </div>
            <p className="text-xs text-muted-foreground">{rep.default_charges_preview.deposit_rule}</p>
            <p className="text-xs text-amber-700 mt-2 font-medium">⚠ {rep.default_charges_preview.warning}</p>
          </Section>

          {/* G. import order */}
          <Section title="G · Recommended Final Import Order" testId="section-order">
            <ol className="list-decimal list-inside text-sm space-y-1">
              {safe.recommended_import_order.map((f) => <li key={f} className="font-mono">{f}</li>)}
            </ol>
            <div className="flex gap-3 mt-4">
              {batch.status === "imported" && (
                <Button variant="destructive" onClick={rollback} disabled={applying} data-testid="rollback-btn">
                  {applying ? "Working…" : "Roll Back This Import"}
                </Button>
              )}
            </div>
          </Section>

          {/* applied result */}
          {batch.status === "imported" && batch.applied_result && (
            <Section title="✓ Final Import Result" defaultOpen testId="section-applied">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {Object.entries(batch.applied_result.counts).map(([k, v]) => (
                  <KV key={k} label={k.replaceAll("_", " ")} value={v} />
                ))}
              </div>
              {batch.applied_result.notes?.map((n, i) => (
                <p key={i} className="text-xs text-muted-foreground mb-1">• {n}</p>
              ))}
              {batch.applied_result.data_issues?.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-amber-700 mb-1">
                    Data issues ({batch.applied_result.data_issues.length}) — fix in-app
                  </p>
                  <div className="max-h-64 overflow-auto rounded border text-xs">
                    {batch.applied_result.data_issues.map((d, i) => (
                      <div key={i} className="border-b last:border-0 px-3 py-1.5">
                        <span className="font-mono text-muted-foreground">{d.file}</span>{" · "}
                        <span className="font-semibold">{d.row_identifier}</span>{" — "}{d.issue}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
